import { ApolloError } from '@apollo/client';
import { diff } from 'deep-object-diff';
import { Formik } from 'formik';
import { isEqual, isNil } from 'lodash';
import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as yup from 'yup';
import {
  RolePermissionForm,
  SortedPermissionFormFields,
  applyFormToRolePermissionFormFields,
  buildRolePermissionsFromForm,
  convertRolePermissionFormFieldsToForm,
  getYupSchemaForRolePermissionForm,
  sortRolePermissionFormData,
} from '../../components/role/role.utils';
import { SaveStatusToast } from '../../components/toast/toast.gql';
import { usePrevious } from '../../util/hooks';
import {
  GetRolePermissionFormDataDocument,
  Role,
  RolePermissionFormField,
  SubscribeToUserPermissionsDocument,
  SubscribeToUserPermissionsSubscriptionResult,
  useGetRoleByTeamQuery,
  useGetRolePermissionFormDataQuery,
  useReplaceRolePermissionsMutation,
} from '../graphql/generated';
import { useApolloSharedSubscription } from '../subscription/apollo-subscription.manager';

interface TeamRoleContextValue {
  role: Role | undefined;
  hasError: boolean;
}

const TeamRoleContext = createContext<TeamRoleContextValue>({
  role: undefined,
  hasError: false,
});

const TeamRoleProvider = (props: {
  children: ReactElement;
  teamId: string;
}): ReactElement => {
  const { children, teamId } = props;

  const {
    data: roleData,
    refetch,
    error,
  } = useGetRoleByTeamQuery({
    variables: {
      input: {
        teamId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const role = roleData?.getRoleByTeam;
  const hasError = !isNil(error);

  const { data } =
    useApolloSharedSubscription<SubscribeToUserPermissionsSubscriptionResult>({
      query: SubscribeToUserPermissionsDocument,
    });

  useEffect(() => {
    if (data?.subscribeToUserPermissions) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data?.subscribeToUserPermissions), refetch]);

  const contextValue: TeamRoleContextValue = useMemo(() => {
    return {
      role,
      hasError,
    };
  }, [hasError, role]);

  return (
    <TeamRoleContext.Provider value={contextValue}>
      {children}
    </TeamRoleContext.Provider>
  );
};

const useTeamRoleContext = (): TeamRoleContextValue => {
  return useContext(TeamRoleContext);
};

interface RoleFormContextValue {
  rolePermissionFormFields: RolePermissionFormField[];
  sortedRolePermissionFormFields: SortedPermissionFormFields;
  updateValidationSchema: (values: RolePermissionForm) => void;
  isRolePermissionFormLoading?: boolean;
  isReplacingRolePermissions?: boolean;
  replaceRolePermissionsError?: ApolloError;
}

const RoleFormContext = createContext<RoleFormContextValue | undefined>(
  undefined
);

interface RoleFormProviderProps {
  children: ReactElement;
  id: string;
}

const RoleFormProvider = (props: RoleFormProviderProps): ReactElement => {
  const { children, id } = props;

  const {
    data: rolePermissionFormData,
    loading: rolePermissionFormLoading,
    error: rolePermissionFormError,
  } = useGetRolePermissionFormDataQuery({
    variables: {
      input: {
        roleId: id,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [
    replaceRolePermissions,
    {
      loading: replaceRolePermissionsLoading,
      error: replaceRolePermissionsError,
    },
  ] = useReplaceRolePermissionsMutation();

  const rolePermissionFormFields: RolePermissionFormField[] = useMemo(() => {
    return rolePermissionFormData?.getRolePermissionFormData ?? [];
  }, [rolePermissionFormData]);

  const initialSchema = useMemo(() => {
    return getYupSchemaForRolePermissionForm({
      rolePermissionFormFields,
    });
  }, [rolePermissionFormFields]);

  const [validationSchema, setValidationSchema] =
    useState<yup.AnySchema>(initialSchema);

  const formValues = useMemo(() => {
    return convertRolePermissionFormFieldsToForm({
      rolePermissionFormFields,
    });
  }, [rolePermissionFormFields]);

  const onSubmit = useCallback(
    async (values: RolePermissionForm) => {
      const rolePermissionsToSave = buildRolePermissionsFromForm({
        roleId: id,
        form: values,
        rolePermissionFormFields,
      });

      await replaceRolePermissions({
        variables: {
          input: {
            roleId: id,
            rolePermissions: rolePermissionsToSave,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GetRolePermissionFormDataDocument,
            variables: {
              input: {
                roleId: id,
              },
            },
          },
        ],
      });
    },
    [id, replaceRolePermissions, rolePermissionFormFields]
  );

  const updateValidationSchema = useCallback(
    (values: RolePermissionForm) => {
      const updatedFormFields = applyFormToRolePermissionFormFields({
        form: values,
        rolePermissionFormFields,
      });
      const updatedSchema = getYupSchemaForRolePermissionForm({
        rolePermissionFormFields: updatedFormFields,
      });

      setValidationSchema(updatedSchema);
    },
    [rolePermissionFormFields]
  );

  // this allows us to ensure we validate the form when the schema changes
  const validateSchemaRef = useRef<() => void>();
  useEffect(() => {
    if (validateSchemaRef.current) {
      validateSchemaRef.current();
    }
  }, [validationSchema]);

  const sortedRolePermissionFormFields = sortRolePermissionFormData({
    rolePermissionFormFields,
  });

  const contextValue = useMemo(() => {
    return {
      rolePermissionFormFields,
      updateValidationSchema,
      isReplacingRolePermissions: replaceRolePermissionsLoading,
      replaceRolePermissionsError,
      isRolePermissionFormLoading: rolePermissionFormLoading,
      sortedRolePermissionFormFields,
    };
  }, [
    rolePermissionFormFields,
    updateValidationSchema,
    replaceRolePermissionsLoading,
    replaceRolePermissionsError,
    rolePermissionFormLoading,
    sortedRolePermissionFormFields,
  ]);

  return (
    <RoleFormContext.Provider value={contextValue}>
      <Formik
        validationSchema={validationSchema}
        initialValues={formValues}
        onSubmit={onSubmit}
        validateOnMount={true}
        enableReinitialize={true}
      >
        {({ validateForm, values }) => {
          /**
           * These hooks are used to ensure the validation schema is updated
           * when the form values change. Moving this to a separate hook and
           * utilizing `values` from the formik context did not work.
           */
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const prev = usePrevious(values);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (!isEqual(prev, values)) {
              const diffed = diff(prev, values);
              // this removes the policies (field names)
              const formChanges = Object.values(diffed);
              // getting the string is a quick way to check the keys
              const diffedString = JSON.stringify(formChanges);

              // only recalculate the schema if the specific fields have changed to
              // avoid unnecessary recalculations
              if (
                diffedString.includes('rolePermissionInputType') ||
                diffedString.includes('can')
              ) {
                updateValidationSchema(values);
              }
            }
          }, [prev, values]);

          validateSchemaRef.current = validateForm;
          return children;
        }}
      </Formik>
      <SaveStatusToast
        loading={replaceRolePermissionsLoading}
        error={replaceRolePermissionsError}
        defaultMessageKey={'common:role_permissions_saved'}
      />
      <SaveStatusToast
        loading={rolePermissionFormLoading}
        error={rolePermissionFormError}
        defaultMessageKey={'common:role_permission_form_failed_to_load'}
        showOnly="error"
      />
    </RoleFormContext.Provider>
  );
};

const useRoleFormContext = (): RoleFormContextValue => {
  const context = useContext(RoleFormContext);

  return context as RoleFormContextValue;
};

export {
  TeamRoleProvider,
  useTeamRoleContext,
  RoleFormProvider,
  useRoleFormContext,
};
